import { ProtectedRoute } from '@/components/auth/protected-route.js';
import {
  CatalogPage,
  type CatalogSearchParams,
} from '@/features/catalog/components/catalog-page.js';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

const catalogSearchSchema = z.object({
  kind: z.enum(['report', 'dashboard']).optional(),
  category: z.string().optional(),
  q: z.string().optional(),
  favoritesOnly: z.boolean().optional(),
  pageNumber: z.number().int().positive().optional(),
  pageSize: z.number().int().positive().optional(),
  view: z.enum(['grid', 'list']).optional(),
});

export const Route = createFileRoute('/catalog')({
  validateSearch: (search) => catalogSearchSchema.parse(search),
  component: CatalogRoute,
});

function buildSearchParams(raw: z.infer<typeof catalogSearchSchema>): CatalogSearchParams {
  const out: CatalogSearchParams = {};
  if (raw.kind !== undefined) out.kind = raw.kind;
  if (raw.category !== undefined) out.category = raw.category;
  if (raw.q !== undefined) out.q = raw.q;
  if (raw.favoritesOnly !== undefined) out.favoritesOnly = raw.favoritesOnly;
  if (raw.pageNumber !== undefined) out.pageNumber = raw.pageNumber;
  if (raw.pageSize !== undefined) out.pageSize = raw.pageSize;
  if (raw.view !== undefined) out.view = raw.view;
  return out;
}

function CatalogRoute() {
  const rawSearch = Route.useSearch();
  const search = buildSearchParams(rawSearch);
  const navigate = useNavigate({ from: '/catalog' });

  function handleSearchParamsChange(next: CatalogSearchParams, replace = true) {
    const searchObj: Record<string, unknown> = {};
    if (next.kind !== undefined) searchObj.kind = next.kind;
    if (next.category !== undefined && next.category.length > 0) searchObj.category = next.category;
    if (next.q !== undefined && next.q.length > 0) searchObj.q = next.q;
    if (next.favoritesOnly === true) searchObj.favoritesOnly = true;
    if (next.pageNumber !== undefined && next.pageNumber > 1)
      searchObj.pageNumber = next.pageNumber;
    if (next.pageSize !== undefined) searchObj.pageSize = next.pageSize;
    if (next.view !== undefined && next.view !== 'grid') searchObj.view = next.view;

    void navigate({
      search: searchObj as z.infer<typeof catalogSearchSchema>,
      replace,
    });
  }

  return (
    <ProtectedRoute>
      <CatalogPage searchParams={search} onSearchParamsChange={handleSearchParamsChange} />
    </ProtectedRoute>
  );
}
